// §6.3 map rendering: hillshade base, value overlay, parcel and building outlines, furniture.

import type { EngineRasters, KeyDay, Ring } from '../../engine/types';
import { t } from '../../i18n';

export type MapLayer = 'annual' | '21. 12.' | '21. 3.' | '21. 6.' | 'veg';

export const RAMP_ANNUAL = ['#2b2d6e', '#3f6fb5', '#5fb3a1', '#e8d35a', '#f08a3c', '#c8342b'];
export const RAMP_HOURS = ['#1f2350', '#3b5fa8', '#7cc0b0', '#f2dc6b', '#f7a14a'];
export const VEG_COLOR = '#2f8f4e';
export const ANNUAL_RANGE: [number, number] = [400, 1450];

const hex = (h: string): [number, number, number] => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];

export function rampColor(stops: string[], t: number): [number, number, number] {
  const c = stops.map(hex);
  const x = Math.max(0, Math.min(1, t)) * (c.length - 1);
  const i = Math.min(c.length - 2, Math.floor(x)), f = x - i;
  return [0, 1, 2].map((k) => c[i][k] + (c[i + 1][k] - c[i][k]) * f) as [number, number, number];
}

export const gradientCss = (stops: string[]) => `linear-gradient(90deg,${stops.join(',')})`;

/**
 * Hillshade 0..1 (azimuth 315°, altitude 40°, vertical exaggeration 1.5).
 * `southUp`: row 0 is the southern edge (engine rasters); otherwise row 0 is north (DSM window).
 */
export function hillshade(z: ArrayLike<number>, w: number, h: number, step: number, southUp: boolean, az = 315, alt = 40, ex = 1.5): Float32Array {
  const out = new Float32Array(w * h);
  const a = (az * Math.PI) / 180, e = (alt * Math.PI) / 180;
  const lx = Math.sin(a) * Math.cos(e), ly = Math.cos(a) * Math.cos(e), lz = Math.sin(e);
  const sgn = southUp ? 1 : -1; // d/dN along rows
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const c = j * w + i;
    const i0 = i > 0 ? i - 1 : i, i1 = i < w - 1 ? i + 1 : i;
    const j0 = j > 0 ? j - 1 : j, j1 = j < h - 1 ? j + 1 : j;
    const dzde = ((z[j * w + i1] - z[j * w + i0]) / ((i1 - i0) * step || 1)) * ex;
    const dzdn = (sgn * (z[j1 * w + i] - z[j0 * w + i]) / ((j1 - j0) * step || 1)) * ex;
    const nz = 1 / Math.hypot(dzde, dzdn, 1);
    out[c] = Math.max(0, (-dzde * lx - dzdn * ly + lz) * nz);
  }
  return out;
}

export interface MapGeometry {
  outer: Ring;                                     // parcel outline (display)
  buildings: { st: number; rings: Ring[] }[];      // buildings on the parcel
}


/** Value of the active layer at a raster cell, or null. */
export function layerValue(r: EngineRasters, layer: MapLayer, c: number): number | null {
  if (layer === 'annual') return r.annual[c];
  if (layer === 'veg') return r.veg[c];
  return r.sunHours[layer as KeyDay][c];
}

/** RGBA image of the domain at one pixel per cell, north-up. */
export function layerImage(r: EngineRasters, layer: MapLayer, shade: Float32Array, astro: number): ImageData {
  const { width: w, height: h } = r;
  const img = new ImageData(w, h);
  const ramp = layer === 'annual' ? RAMP_ANNUAL : RAMP_HOURS;
  const [v0, v1] = layer === 'annual' ? ANNUAL_RANGE : [0, astro || 1];
  const veg = hex(VEG_COLOR);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const c = j * w + i, p = ((h - 1 - j) * w + i) * 4;
    const g = 70 + 185 * shade[c];
    let col: [number, number, number] | null = null, a = 0;
    if (layer === 'veg') { if (r.veg[c]) { col = veg; a = 0.6; } }
    else { col = rampColor(ramp, ((layerValue(r, layer, c) ?? 0) - v0) / (v1 - v0)); a = r.parcel[c] ? 0.9 : 0.38; }
    img.data[p] = col ? g * (1 - a) + col[0] * a : g;
    img.data[p + 1] = col ? g * (1 - a) + col[1] * a : g;
    img.data[p + 2] = col ? g * (1 - a) + col[2] * a : g;
    img.data[p + 3] = 255;
  }
  return img;
}

export interface Placement { k: number; w: number; h: number; e0: number; nTop: number } // k = px per metre (CSS px)

export function placement(r: EngineRasters, cssWidth: number, maxPxPerM = 8): Placement {
  const wm = r.width * r.step, hm = r.height * r.step;
  const k = Math.min(maxPxPerM, cssWidth / wm);
  return { k, w: Math.round(wm * k), h: Math.round(hm * k), e0: r.originE, nTop: r.originS + hm };
}

/** Draw the full map (base image + vectors + furniture) into `canvas` at device resolution. */
export function drawMap(
  canvas: HTMLCanvasElement, img: ImageData, geo: MapGeometry, pl: Placement, dpr: number,
  labels: { north: string; scale: string }, ink = '#1b2430',
) {
  canvas.width = Math.round(pl.w * dpr); canvas.height = Math.round(pl.h * dpr);
  canvas.style.aspectRatio = `${pl.w} / ${pl.h}`;
  const g = canvas.getContext('2d')!;
  const src = document.createElement('canvas');
  src.width = img.width; src.height = img.height;
  src.getContext('2d')!.putImageData(img, 0, 0);
  g.imageSmoothingEnabled = false;
  g.drawImage(src, 0, 0, canvas.width, canvas.height);
  g.save();
  g.scale(dpr, dpr);
  const X = (e: number) => (e - pl.e0) * pl.k, Y = (n: number) => (pl.nTop - n) * pl.k;
  const path = (ring: Ring) => { g.beginPath(); ring.forEach(([e, n], i) => (i ? g.lineTo(X(e), Y(n)) : g.moveTo(X(e), Y(n)))); g.closePath(); };

  // buildings on parcel
  g.lineJoin = 'round';
  for (const b of geo.buildings) {
    for (const ring of b.rings) { path(ring); g.strokeStyle = 'rgba(255,255,255,.95)'; g.lineWidth = 2.4; g.stroke(); g.strokeStyle = ink; g.lineWidth = 1; g.stroke(); }
    const ring = b.rings[0];
    let sx = 0, sy = 0; const m = ring.length - 1 || 1;
    for (let q = 0; q < m; q++) { sx += ring[q][0]; sy += ring[q][1]; }
    const lx = X(sx / m), ly = Y(sy / m);
    g.font = '600 12px "IBM Plex Mono", ui-monospace, monospace';
    g.textAlign = 'center'; g.textBaseline = 'middle';
    const tw = g.measureText(t.bShort(b.st)).width + 8;
    g.fillStyle = 'rgba(255,255,255,.92)'; g.fillRect(lx - tw / 2, ly - 9, tw, 18);
    g.fillStyle = ink; g.fillText(t.bShort(b.st), lx, ly + 0.5);
  }
  // parcel outline: 2.2 px dark + 1 px white dashed
  path(geo.outer); g.strokeStyle = ink; g.lineWidth = 2.2; g.setLineDash([]); g.stroke();
  g.strokeStyle = '#fff'; g.lineWidth = 1; g.setLineDash([5, 4]); g.stroke(); g.setLineDash([]);

  // north arrow
  const ax = pl.w - 22, ay = 14;
  g.fillStyle = 'rgba(255,255,255,.88)'; g.beginPath(); g.arc(ax, ay + 14, 15, 0, Math.PI * 2); g.fill();
  g.fillStyle = ink; g.beginPath(); g.moveTo(ax, ay + 2); g.lineTo(ax + 6, ay + 18); g.lineTo(ax, ay + 14); g.lineTo(ax - 6, ay + 18); g.closePath(); g.fill();
  g.font = '600 10px "IBM Plex Mono", ui-monospace, monospace'; g.textAlign = 'center'; g.textBaseline = 'alphabetic';
  g.fillText(labels.north, ax, ay + 27);

  // 20 m scale bar
  const sw = 20 * pl.k, sx0 = 12, sy0 = pl.h - 14;
  g.fillStyle = 'rgba(255,255,255,.88)'; g.fillRect(sx0 - 6, sy0 - 16, sw + 12, 24);
  g.fillStyle = ink; g.fillRect(sx0, sy0, sw, 3); g.fillRect(sx0, sy0 - 4, 1.5, 7); g.fillRect(sx0 + sw - 1.5, sy0 - 4, 1.5, 7);
  g.font = '500 10px "IBM Plex Mono", ui-monospace, monospace'; g.textAlign = 'center';
  g.fillText(labels.scale, sx0 + sw / 2, sy0 - 4);
  g.restore();
}

/** Raster cell under CSS pixel (x, y) of a placed map, or -1. */
export function cellAt(r: EngineRasters, pl: Placement, x: number, y: number): number {
  const e = pl.e0 + x / pl.k, n = pl.nTop - y / pl.k;
  const i = Math.floor((e - r.originE) / r.step), j = Math.floor((n - r.originS) / r.step);
  if (i < 0 || j < 0 || i >= r.width || j >= r.height) return -1;
  return j * r.width + i;
}
